import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Stack,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  AddCircleOutline as AddCircleIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import ConditionalRender from '../common/ConditionalRender';
import CurrencyInput from '../common/CurrencyInput';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const SavingsStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { savings, personal } = wizardData;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);

  // Initialize progress tracking
  const { trackFieldChange } = useProgressTracking({
    stepName: 'Savings & Assets',
    stepNumber: 4
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Helper to get balance for specific account type and owner
  const getBalance = (assetType: 'taxDeferred' | 'taxFree' | 'taxable', owner: 'client1' | 'client2' | 'joint'): number => {
    const accounts = savings[assetType];
    const account = accounts.find(acc => acc.owner === owner);
    return account?.balance || 0;
  };

  // Helper to update or create account entry
  const handleAssetChange = (
    assetType: 'taxDeferred' | 'taxFree' | 'taxable',
    owner: 'client1' | 'client2' | 'joint',
    value: string
  ) => {
    const numValue = value === '' ? 0 : Number(value);
    const accounts = [...savings[assetType]];

    // Find existing account for this owner
    const existingIndex = accounts.findIndex(acc => acc.owner === owner);

    // Generate generic account name based on type
    const accountName = assetType === 'taxDeferred'
      ? 'Tax-Deferred Retirement Accounts'
      : assetType === 'taxFree'
      ? 'Tax-Free Retirement Accounts'
      : 'Taxable Accounts';

    if (existingIndex >= 0) {
      // Update existing account
      if (numValue > 0) {
        accounts[existingIndex] = { ...accounts[existingIndex], balance: numValue };
      } else {
        // Remove account if balance is 0
        accounts.splice(existingIndex, 1);
      }
    } else if (numValue > 0) {
      // Add new account
      accounts.push({
        accountName,
        balance: numValue,
        owner
      });
    }

    updateWizardData({
      savings: {
        ...savings,
        [assetType]: accounts
      }
    });
  };

  // Calculate totals
  const clientTotal = getBalance('taxDeferred', 'client1') +
                      getBalance('taxFree', 'client1') +
                      (personal.hasPartner ? 0 : getBalance('taxable', 'client1'));

  const partnerTotal = personal.hasPartner
    ? getBalance('taxDeferred', 'client2') + getBalance('taxFree', 'client2')
    : 0;

  const jointTotal = personal.hasPartner ? getBalance('taxable', 'joint') : 0;

  const grandTotal = clientTotal + partnerTotal + jointTotal;

  const isSingleClient = !personal.hasPartner;

  // Mobile version with accordions
  const renderMobileView = () => (
    <>
      {personal.hasPartner && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={personal.client1.firstName} />
          <Tab label={personal.client2.firstName} />
          <Tab label="Joint" />
        </Tabs>
      )}

      {/* Tax-Deferred Accounts */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">
            Tax-Deferred Retirement Accounts
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 2 }}>
            (401k, 403b, Traditional IRA, etc.)
          </Typography>

          {/* Client's tax-deferred accounts */}
          {(!personal.hasPartner || activeTab === 0) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {personal.hasPartner ? `${personal.client1.firstName}'s Accounts:` : 'Your Accounts:'}
              </Typography>
              <CurrencyInput
                fullWidth
                size="small"
                value={getBalance('taxDeferred', 'client1') || ''}
                onChange={(value) => handleAssetChange('taxDeferred', 'client1', value.toString())}
              />
            </Box>
          )}

          {/* Partner's tax-deferred accounts */}
          {personal.hasPartner && activeTab === 1 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {personal.client2.firstName}'s Accounts:
              </Typography>
              <CurrencyInput
                fullWidth
                size="small"
                value={getBalance('taxDeferred', 'client2') || ''}
                onChange={(value) => handleAssetChange('taxDeferred', 'client2', value.toString())}
              />
            </Box>
          )}

          {/* Joint tab for tax-deferred (N/A) */}
          {personal.hasPartner && activeTab === 2 && (
            <Typography variant="body2" color="text.secondary">
              Tax-deferred accounts are typically owned individually, not jointly.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Tax-Free Accounts */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">
            Tax-Free Retirement Accounts
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 2 }}>
            (Roth IRA, Roth 401k, etc.)
          </Typography>

          {/* Client's tax-free accounts */}
          {(!personal.hasPartner || activeTab === 0) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {personal.hasPartner ? `${personal.client1.firstName}'s Accounts:` : 'Your Accounts:'}
              </Typography>
              <CurrencyInput
                fullWidth
                size="small"
                value={getBalance('taxFree', 'client1') || ''}
                onChange={(value) => handleAssetChange('taxFree', 'client1', value.toString())}
              />
            </Box>
          )}

          {/* Partner's tax-free accounts */}
          {personal.hasPartner && activeTab === 1 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {personal.client2.firstName}'s Accounts:
              </Typography>
              <CurrencyInput
                fullWidth
                size="small"
                value={getBalance('taxFree', 'client2') || ''}
                onChange={(value) => handleAssetChange('taxFree', 'client2', value.toString())}
              />
            </Box>
          )}

          {/* Joint tab for tax-free (N/A) */}
          {personal.hasPartner && activeTab === 2 && (
            <Typography variant="body2" color="text.secondary">
              Tax-free accounts are typically owned individually, not jointly.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Taxable Accounts */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">
            Taxable Accounts
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 2 }}>
            (Brokerage, Savings, Checking, CDs, etc.)
          </Typography>

          {/* For single clients */}
          {!personal.hasPartner && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Your Accounts:
              </Typography>
              <CurrencyInput
                fullWidth
                size="small"
                value={getBalance('taxable', 'client1') || ''}
                onChange={(value) => handleAssetChange('taxable', 'client1', value.toString())}
              />
            </Box>
          )}

          {/* For couples */}
          {personal.hasPartner && (
            <>
              {(activeTab === 0 || activeTab === 1) && (
                <Typography variant="body2" color="text.secondary">
                  For couples, please enter taxable accounts as joint.
                </Typography>
              )}

              {activeTab === 2 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Joint Accounts:
                  </Typography>
                  <CurrencyInput
                    fullWidth
                    size="small"
                    value={getBalance('taxable', 'joint') || ''}
                    onChange={(value) => handleAssetChange('taxable', 'joint', value.toString())}
                  />
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Mobile Totals Summary */}
      <Card sx={{ mt: 3, mb: 3, bgcolor: 'background.paper', boxShadow: 1 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Total Savings
          </Typography>
          
          {!personal.hasPartner ? (
            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography>Your Total:</Typography>
              <Typography fontWeight="bold">${clientTotal.toLocaleString()}</Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography>{personal.client1.firstName}'s Total:</Typography>
                <Typography fontWeight="bold">${clientTotal.toLocaleString()}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography>{personal.client2.firstName}'s Total:</Typography>
                <Typography fontWeight="bold">${partnerTotal.toLocaleString()}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography>Joint Accounts:</Typography>
                <Typography fontWeight="bold">${jointTotal.toLocaleString()}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
            </>
          )}
          
          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography fontWeight="bold">Grand Total:</Typography>
            <Typography fontWeight="bold" color={grandTotal > 0 ? "primary" : "error"}>
              ${grandTotal.toLocaleString()}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </>
  );

  // Desktop version with table
  const renderDesktopView = () => (
    <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Account Type</TableCell>
              {personal.hasPartner ? (
                <>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{personal.client1.firstName}'s Accounts</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{personal.client2.firstName}'s Accounts</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Joint Accounts</TableCell>
                </>
              ) : (
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Your Accounts</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="body2">
                  Tax-Deferred Retirement Accounts
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    (401k, 403b, Traditional IRA, etc.)
                  </Typography>
                </Typography>
              </TableCell>
              <TableCell align="right">
                <CurrencyInput
                  size="small"
                  value={getBalance('taxDeferred', 'client1') || ''}
                  onChange={(value) => handleAssetChange('taxDeferred', 'client1', value.toString())}
                />
              </TableCell>
              <ConditionalRender show={personal.hasPartner}>
                <>
                  <TableCell align="right">
                    <CurrencyInput
                      size="small"
                      value={getBalance('taxDeferred', 'client2') || ''}
                      onChange={(value) => handleAssetChange('taxDeferred', 'client2', value.toString())}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                  </TableCell>
                </>
              </ConditionalRender>
            </TableRow>

            <TableRow>
              <TableCell>
                <Typography variant="body2">
                  Tax-Free Retirement Accounts
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    (Roth IRA, Roth 401k, etc.)
                  </Typography>
                </Typography>
              </TableCell>
              <TableCell align="right">
                <CurrencyInput
                  size="small"
                  value={getBalance('taxFree', 'client1') || ''}
                  onChange={(value) => handleAssetChange('taxFree', 'client1', value.toString())}
                />
              </TableCell>
              <ConditionalRender show={personal.hasPartner}>
                <>
                  <TableCell align="right">
                    <CurrencyInput
                      size="small"
                      value={getBalance('taxFree', 'client2') || ''}
                      onChange={(value) => handleAssetChange('taxFree', 'client2', value.toString())}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                  </TableCell>
                </>
              </ConditionalRender>
            </TableRow>

            <TableRow>
              <TableCell>
                <Typography variant="body2">
                  Taxable Accounts
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    (Brokerage, Savings, Checking, CDs, etc.)
                  </Typography>
                </Typography>
              </TableCell>
              <ConditionalRender show={personal.hasPartner}>
                <>
                  <TableCell align="right" colSpan={2}>
                    <Typography variant="body2" color="text.secondary">For couples, please enter taxable accounts as joint</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <CurrencyInput
                      size="small"
                      value={getBalance('taxable', 'joint') || ''}
                      onChange={(value) => handleAssetChange('taxable', 'joint', value.toString())}
                    />
                  </TableCell>
                </>
              </ConditionalRender>
              <ConditionalRender show={!personal.hasPartner}>
                <TableCell align="right">
                  <CurrencyInput
                    size="small"
                    value={getBalance('taxable', 'client1') || ''}
                    onChange={(value) => handleAssetChange('taxable', 'client1', value.toString())}
                  />
                </TableCell>
              </ConditionalRender>
            </TableRow>

            {/* Totals Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                ${clientTotal.toLocaleString()}
              </TableCell>
              {personal.hasPartner && (
                <>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    ${partnerTotal.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    ${jointTotal.toLocaleString()}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Savings & Investments
      </Typography>

      <Typography variant="body1" paragraph sx={{ mb: 4 }}>
        Please enter the total value of your investment accounts by category.
        {personal.hasPartner && " For taxable accounts (like checking, savings, or brokerage accounts), please only enter joint accounts."}
      </Typography>

      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Grand Total (desktop only) */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Grand Total:</Typography>
            <Typography variant="h6" color={grandTotal > 0 ? "primary" : "error"}>
              ${grandTotal.toLocaleString()}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Validation message
      {grandTotal <= 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="error">
            Please enter at least one account balance to continue.
          </Typography>
        </Box>
      )} */}

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Note: These values will be used as a starting point for your financial planning session.
          Your advisor will help you refine these numbers during your meeting.
        </Typography>
      </Box>
    </Box>
  );
};

export default SavingsStep; 